<script setup lang="ts">
import type { FormField } from '#shared/types/schema';
import { useField } from 'vee-validate';
import { Info } from 'lucide-vue-next';

import Input from '~/components/ui/input/Input.vue';
import { Textarea } from '~/components/ui/textarea';
import CheckboxField from './fields/CheckboxField.vue';
import CheckboxGroupField from './fields/CheckboxGroupField.vue';
import RadioGroupField from './fields/RadioGroupField.vue';
import SelectField from './fields/SelectField.vue';
import FileUploadField from './fields/FileUploadField.vue';

const props = withDefaults(defineProps<{ 
	field: FormField;
	labelColor?: string;
}>(), {
	labelColor: 'text-gray-900',
});
const { value, errorMessage } = useField(props.field.name ?? '');

const componentMap: Record<string, Component> = {
	textarea: Textarea,
	checkbox: CheckboxField,
	checkbox_group: CheckboxGroupField,
	radio: RadioGroupField,
	select: SelectField,
	file: FileUploadField,
};

const getFieldComponent = () => componentMap[props.field.type ?? ''] || Input;

// Determina se mostrare la label (non mostrare per checkbox o se non c'è label né help)
const shouldShowLabel = computed(() => {
	if (props.field.type === 'checkbox') {
		return false; // I checkbox hanno la label dentro il componente
	}
	return !!(props.field.label || props.field.help);
});

const getComponentProps = (field: FormField) => {
	// Assicurati che modelValue sia sempre del tipo corretto
	let modelValue: any = value.value;
	
	if (field.type === 'checkbox_group') {
		// Per checkbox_group, assicurati che sia sempre un array
		modelValue = Array.isArray(value.value) ? value.value : [];
	} else if (['radio', 'select'].includes(field.type ?? '')) {
		// Per radio/select, assicurati che sia sempre una stringa
		modelValue = value.value ?? '';
	}
	
	const baseProps = {
		id: field.id,
		name: field.name ?? '',
		placeholder: field.placeholder ?? '',
		modelValue: modelValue,
		'onUpdate:modelValue': (val: any) => (value.value = val),
	};

	if (['checkbox_group', 'radio', 'select'].includes(field.type ?? '')) {
		return { ...baseProps, options: field.choices ?? [] };
	}

	if (field.type === 'checkbox') {
		return { ...baseProps, label: field.label ?? '', labelColor: props.labelColor };
	}

	return baseProps;
};
</script>

<template>
	<div v-if="props.field.type !== 'hidden'" :class="`field-width-${field.width ?? '100'}`">
		<FormItem class="pt-2">
			<FormLabel v-if="shouldShowLabel" :for="field.name ?? ''" :class="['flex items-center mb-4 md:mb-4', labelColor]">
				<div class="flex items-center space-x-1 min-h-[20px]">
					<span v-if="field.label" class="text-base font-bold leading-tight">{{ field.label }}</span>
					<TooltipProvider v-if="field.help">
						<Tooltip>
							<TooltipTrigger>
								<Info :class="['w-4 h-4 cursor-pointer flex-shrink-0', labelColor === 'text-white' ? 'text-white/70' : 'text-gray-500']" />
							</TooltipTrigger>
							<TooltipContent>{{ field.help }}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			</FormLabel>
			<FormControl class="mt-2">
				<component :is="getFieldComponent()" v-bind="getComponentProps(field)" />
			</FormControl>
			<FormMessage v-if="errorMessage" class="text-red-500 italic text-sm">{{ errorMessage }}</FormMessage>
		</FormItem>
	</div>
</template>

<style scoped>
.field-width-100 {
	flex: 100%;
}
.field-width-50 {
	flex: 100%;
}
.field-width-67 {
	flex: 100%;
}
.field-width-33 {
	flex: 100%;
}

/* Su desktop, applica le larghezze specificate */
@media (min-width: 768px) {
	.field-width-50 {
		flex: calc(50% - 1rem);
	}
	.field-width-67 {
		flex: calc(67% - 1rem);
	}
	.field-width-33 {
		flex: calc(33% - 1rem);
	}
}
</style>
